import { useState } from 'react';
import { ArrowLeftIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Card, Badge, Button, Input, Spinner } from '@realvisor/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import {
  useCampaign,
  useCampaignStats,
  useCampaignOperatorStats,
  useCampaignQueue,
  useAssignBatch,
  useUpdateCampaign,
  usePopulateCampaign,
  usePendingRewards,
  useRewardsSummary,
  useApproveRewards,
  useRejectRewards,
  type CampaignQueueContact,
  type CampaignOutcome,
} from '@/hooks/useCampaigns';

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'info' }> = {
  draft: { label: 'Koncept', variant: 'default' },
  active: { label: 'Aktivní', variant: 'success' },
  paused: { label: 'Pozastaveno', variant: 'warning' },
  completed: { label: 'Dokončeno', variant: 'info' },
};

const STATUS_TRANSITIONS: Record<string, { value: string; label: string }[]> = {
  draft: [{ value: 'active', label: 'Aktivovat' }],
  active: [
    { value: 'paused', label: 'Pozastavit' },
    { value: 'completed', label: 'Dokončit' },
  ],
  paused: [
    { value: 'active', label: 'Obnovit' },
    { value: 'completed', label: 'Dokončit' },
  ],
  completed: [],
};

const OUTCOME_CODES = [
  { value: 'interested', label: 'interested' },
  { value: 'meeting_set', label: 'meeting_set' },
  { value: 'meeting_done', label: 'meeting_done' },
  { value: 'not_interested', label: 'not_interested' },
  { value: 'unavailable', label: 'unavailable' },
  { value: 'callback', label: 'callback' },
  { value: 'wrong_number', label: 'wrong_number' },
];

const QUEUE_STATUS_LABELS: Record<string, string> = {
  pending: 'Čeká',
  assigned: 'Přiřazeno',
  in_progress: 'Probíhá',
  completed: 'Dokončeno',
  callback: 'Callback',
};

interface OutcomeRow {
  name: string;
  code: string;
  rewardAmount: number | '';
  createsLead: boolean;
  removesFromQueue: boolean;
  followUpDays: number | '';
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

type Tab = 'overview' | 'operators' | 'queue' | 'rewards';

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [queueStatusFilter, setQueueStatusFilter] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { can } = usePermissions();
  const canManage = can('campaigns:manage');

  const { data: campaign, isLoading, error } = useCampaign(id || '');
  const { data: stats } = useCampaignStats(id || '', canManage);
  const { data: operatorStats } = useCampaignOperatorStats(id || '', canManage);
  const { data: queue } = useCampaignQueue(id || '');
  const assignBatch = useAssignBatch();
  const updateCampaign = useUpdateCampaign();
  const populateCampaign = usePopulateCampaign();

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editBatchSize, setEditBatchSize] = useState(25);
  const [editScriptContent, setEditScriptContent] = useState('');

  const startEditing = () => {
    if (!campaign) return;
    setEditName(campaign.name);
    setEditDescription(campaign.description || '');
    setEditBatchSize(campaign.batchSize);
    setEditScriptContent(campaign.scriptContent || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveEditing = async () => {
    if (!campaign || !editName.trim()) {
      toast.error('Název kampaně je povinný');
      return;
    }
    try {
      await updateCampaign.mutateAsync({
        id: campaign.id,
        data: {
          name: editName.trim(),
          description: editDescription.trim() || undefined,
          batchSize: editBatchSize,
          scriptContent: editScriptContent.trim() || undefined,
        },
      });
      toast.success('Kampaň aktualizována');
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se uložit změny');
    }
  };

  const changeStatus = async (newStatus: string) => {
    if (!campaign) return;
    try {
      await updateCampaign.mutateAsync({
        id: campaign.id,
        data: { status: newStatus },
      });
      toast.success(`Status změněn na ${STATUS_BADGES[newStatus]?.label || newStatus}`);
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se změnit status');
    }
  };

  const handlePopulate = async () => {
    if (!campaign) return;
    try {
      await populateCampaign.mutateAsync(campaign.id);
      toast.success('Kontakty naplněny z projektu');
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se naplnit kontakty');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Kampaň nebyla nalezena</p>
          <Link to="/callcenter/campaigns" className="mt-4 inline-block text-primary-600 hover:underline">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  const badge = STATUS_BADGES[campaign.status] || STATUS_BADGES['draft'];
  const totalContacts = stats?.total ?? campaign._count?.contacts ?? 0;
  const completedContacts = stats?.completed ?? 0;
  const pct = totalContacts > 0 ? Math.round((completedContacts / totalContacts) * 100) : 0;
  const transitions = STATUS_TRANSITIONS[campaign.status] || [];

  const filteredQueue = queue
    ? queueStatusFilter
      ? queue.filter((q) => q.status === queueStatusFilter)
      : queue
    : [];

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Přehled' },
    { key: 'operators', label: 'Operátoři' },
    { key: 'queue', label: 'Fronta' },
    { key: 'rewards', label: 'Odměny' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/callcenter/campaigns"
          className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Název</label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Název kampaně"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Popis kampaně"
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Velikost baliku</label>
                <Input
                  type="number"
                  value={editBatchSize}
                  onChange={(e) => setEditBatchSize(Number(e.target.value))}
                  min={1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Call skript</label>
                <textarea
                  value={editScriptContent}
                  onChange={(e) => setEditScriptContent(e.target.value)}
                  placeholder="Text call skriptu, ktery operator vidi pri volani..."
                  rows={8}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">Tento text se zobrazi operatorovi primo pri volani v pravem panelu.</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEditing} disabled={updateCampaign.isPending}>
                  {updateCampaign.isPending ? <Spinner size="sm" className="mr-1" /> : <CheckIcon className="h-4 w-4 mr-1" />}
                  Uložit
                </Button>
                <Button variant="outline" size="sm" onClick={cancelEditing}>
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Zrušit
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
                <Badge variant={badge!.variant}>{badge!.label}</Badge>
              </div>
              <p className="text-sm text-gray-500">
                {campaign.project?.name || 'Bez projektu'}
                {campaign.description && ` – ${campaign.description}`}
              </p>
            </>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-start">
          {canManage && !isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={startEditing}>
                <PencilIcon className="h-4 w-4 mr-1" />
                Upravit
              </Button>
              {transitions.map((t) => (
                <Button
                  key={t.value}
                  variant="outline"
                  size="sm"
                  onClick={() => changeStatus(t.value)}
                  disabled={updateCampaign.isPending}
                >
                  {t.label}
                </Button>
              ))}
            </>
          )}
          {canManage && totalContacts === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePopulate}
              disabled={populateCampaign.isPending}
            >
              {populateCampaign.isPending ? <Spinner size="sm" className="mr-1" /> : <PlusIcon className="h-4 w-4 mr-1" />}
              Naplnit kontakty z projektu
            </Button>
          )}
          <Link to={`/callcenter/campaigns/${campaign.id}/call`}>
            <Button>Volat</Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          campaignId={campaign.id}
          outcomes={campaign.outcomes}
          scriptContent={campaign.scriptContent}
          stats={stats}
          totalContacts={totalContacts}
          completedContacts={completedContacts}
          pct={pct}
          canManage={canManage}
        />
      )}

      {activeTab === 'operators' && (
        <OperatorsTab
          campaignId={campaign.id}
          campaign={campaign}
          operatorStats={operatorStats}
          assignBatch={assignBatch}
          canManage={canManage}
        />
      )}

      {activeTab === 'queue' && (
        <QueueTab
          queue={filteredQueue}
          statusFilter={queueStatusFilter}
          onStatusFilterChange={setQueueStatusFilter}
        />
      )}

      {activeTab === 'rewards' && campaign && (
        <RewardsTab campaignId={campaign.id} />
      )}
    </div>
  );
}

// --- Přehled tab ---

function OverviewTab({
  campaignId,
  outcomes,
  scriptContent,
  stats,
  totalContacts,
  completedContacts,
  pct,
  canManage,
}: {
  campaignId: string;
  outcomes: CampaignOutcome[] | undefined;
  scriptContent: string | null;
  stats: ReturnType<typeof useCampaignStats>['data'];
  totalContacts: number;
  completedContacts: number;
  pct: number;
  canManage: boolean;
}) {
  const [editingOutcomes, setEditingOutcomes] = useState(false);
  const [outcomeRows, setOutcomeRows] = useState<OutcomeRow[]>([]);
  const updateCampaign = useUpdateCampaign();

  const startOutcomeEdit = () => {
    const rows: OutcomeRow[] = (outcomes || []).map((o: any) => ({
      name: o.name,
      code: o.code,
      rewardAmount: o.rewardAmount ?? '',
      createsLead: o.createsLead,
      removesFromQueue: o.removesFromQueue,
      followUpDays: o.followUpDays ?? '',
    }));
    setOutcomeRows(rows);
    setEditingOutcomes(true);
  };

  const updateOutcome = (index: number, field: keyof OutcomeRow, value: any) => {
    setOutcomeRows((prev) => prev.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
  };

  const addOutcome = () => {
    setOutcomeRows((prev) => [
      ...prev,
      { name: '', code: 'interested', rewardAmount: '', createsLead: false, removesFromQueue: false, followUpDays: '' },
    ]);
  };

  const removeOutcome = (index: number) => {
    setOutcomeRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveOutcomes = async () => {
    const cleaned = outcomeRows
      .filter((o) => o.name.trim() && o.code)
      .map((o) => ({
        name: o.name.trim(),
        code: o.code,
        rewardAmount: o.rewardAmount !== '' ? Number(o.rewardAmount) : undefined,
        createsLead: o.createsLead,
        removesFromQueue: o.removesFromQueue,
        followUpDays: o.followUpDays !== '' ? Number(o.followUpDays) : undefined,
      }));

    try {
      await updateCampaign.mutateAsync({
        id: campaignId,
        data: { outcomes: cleaned },
      });
      toast.success('Výsledky uloženy');
      setEditingOutcomes(false);
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se uložit výsledky');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card padding="md">
          <p className="text-sm text-gray-500">Celkem kontaktů</p>
          <p className="text-2xl font-bold text-gray-900">{totalContacts}</p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Provoláno</p>
          <p className="text-2xl font-bold text-green-600">{completedContacts}</p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Zbývá</p>
          <p className="text-2xl font-bold text-gray-900">
            {totalContacts - completedContacts}
          </p>
        </Card>
        <Card padding="md">
          <p className="text-sm text-gray-500">Callbacks</p>
          <p className="text-2xl font-bold text-orange-600">{stats?.callback ?? 0}</p>
        </Card>
      </div>

      {/* Progress bar */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Celkový průběh</span>
          <span className="text-sm text-gray-500">{pct}%</span>
        </div>
        <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </Card>

      {/* Outcomes breakdown / editing */}
      {editingOutcomes ? (
        <Card padding="md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Upravit výsledky hovorů</h2>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={addOutcome}>
                <PlusIcon className="h-4 w-4 mr-1" />
                Přidat výsledek
              </Button>
            </div>
          </div>

          {outcomeRows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Žádné výsledky. Přidejte alespoň jeden.</p>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="hidden sm:grid sm:grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider px-1">
                <div className="col-span-2">Název</div>
                <div className="col-span-2">Kód</div>
                <div className="col-span-1">Odměna Kč</div>
                <div className="col-span-1">Follow-up dní</div>
                <div className="col-span-2 text-center">Vytvoří lead</div>
                <div className="col-span-2 text-center">Vyřadí z fronty</div>
                <div className="col-span-2" />
              </div>

              {outcomeRows.map((outcome, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 rounded-lg bg-gray-50"
                >
                  <div className="sm:col-span-2">
                    <Input
                      value={outcome.name}
                      onChange={(e) => updateOutcome(index, 'name', e.target.value)}
                      placeholder="Název"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <select
                      value={outcome.code}
                      onChange={(e) => updateOutcome(index, 'code', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm"
                    >
                      {OUTCOME_CODES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-1">
                    <Input
                      type="number"
                      value={outcome.rewardAmount}
                      onChange={(e) =>
                        updateOutcome(index, 'rewardAmount', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Input
                      type="number"
                      value={outcome.followUpDays}
                      onChange={(e) =>
                        updateOutcome(index, 'followUpDays', e.target.value === '' ? '' : Number(e.target.value))
                      }
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-center">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={outcome.createsLead}
                        onChange={(e) => updateOutcome(index, 'createsLead', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="sm:hidden">Vytvoří lead</span>
                    </label>
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-center">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={outcome.removesFromQueue}
                        onChange={(e) => updateOutcome(index, 'removesFromQueue', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="sm:hidden">Vyřadí z fronty</span>
                    </label>
                  </div>
                  <div className="sm:col-span-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeOutcome(index)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setEditingOutcomes(false)}>
              Zrušit
            </Button>
            <Button size="sm" onClick={saveOutcomes} disabled={updateCampaign.isPending}>
              {updateCampaign.isPending ? <Spinner size="sm" className="mr-1" /> : null}
              Uložit
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Read-only outcomes view */}
          <Card padding="none">
            <div className="border-b border-gray-200 px-6 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Výsledky hovorů</h2>
              {canManage && (
                <Button variant="outline" size="sm" onClick={startOutcomeEdit}>
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Upravit výsledky
                </Button>
              )}
            </div>
            {stats?.outcomes && stats.outcomes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Výsledek
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Počet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Celkové odměny
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {stats.outcomes.map((outcome) => (
                      <tr key={outcome.outcomeId}>
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                          {outcome.name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {outcome.count}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {outcome.totalRewards > 0 ? `${outcome.totalRewards.toLocaleString()} Kč` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-sm text-gray-500">
                Zatím žádné výsledky hovorů
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// --- Operátoři tab ---

function OperatorsTab({
  campaignId,
  campaign,
  operatorStats,
  assignBatch,
  canManage,
}: {
  campaignId: string;
  campaign: ReturnType<typeof useCampaign>['data'];
  operatorStats: ReturnType<typeof useCampaignOperatorStats>['data'];
  assignBatch: ReturnType<typeof useAssignBatch>;
  canManage: boolean;
}) {
  const operators = operatorStats?.operators ?? [];
  const updateCampaign = useUpdateCampaign();
  const [selectedUserId, setSelectedUserId] = useState('');

  // Fetch available CC users
  const { data: usersData } = useQuery({
    queryKey: ['users', 'ccOnly'],
    queryFn: () => api.get<{ data: Array<{ id: string; firstName: string; lastName: string; email: string }> } | Array<{ id: string; firstName: string; lastName: string; email: string }>>('/users?ccOnly=true'),
    enabled: canManage,
  });

  const availableUsers = Array.isArray(usersData) ? usersData : usersData?.data ?? [];

  // Filter out already assigned operators
  const currentOperatorIds = campaign?.operators?.map((op) => op.userId) ?? [];
  const unassignedUsers = availableUsers.filter((u) => !currentOperatorIds.includes(u.id));

  const addOperator = async () => {
    if (!selectedUserId || !campaign) return;
    const newIds = [...currentOperatorIds, selectedUserId];
    try {
      await updateCampaign.mutateAsync({
        id: campaignId,
        data: { operatorIds: newIds },
      });
      toast.success('Operátor přidán');
      setSelectedUserId('');
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se přidat operátora');
    }
  };

  const removeOperator = async (userId: string) => {
    if (!campaign) return;
    const newIds = currentOperatorIds.filter((oid) => oid !== userId);
    try {
      await updateCampaign.mutateAsync({
        id: campaignId,
        data: { operatorIds: newIds },
      });
      toast.success('Operátor odebrán');
    } catch (err: any) {
      toast.error(err.message || 'Nepodařilo se odebrat operátora');
    }
  };

  return (
    <div className="space-y-4">
      {/* Add operator */}
      {canManage && (
        <Card padding="md">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Přidat operátora</h3>
          <div className="flex gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Vyberte operátora</option>
              {unassignedUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.email})
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={addOperator}
              disabled={!selectedUserId || updateCampaign.isPending}
            >
              {updateCampaign.isPending ? <Spinner size="sm" className="mr-1" /> : <PlusIcon className="h-4 w-4 mr-1" />}
              Přidat
            </Button>
          </div>
        </Card>
      )}

      {/* Operators table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Jméno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Hovory
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Délka
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Odměny
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {operators.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                    Zatím žádní operátoři
                  </td>
                </tr>
              ) : (
                operators.map((op) => (
                  <tr key={op.userId}>
                    <td className="whitespace-nowrap px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">
                        {op.firstName} {op.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{op.fullName}</p>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {op.calls}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatDuration(op.totalDuration)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {op.totalRewards > 0 ? `${op.totalRewards.toLocaleString()} Kč` : '-'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => assignBatch.mutate({ campaignId, userId: op.userId })}
                          disabled={assignBatch.isPending}
                        >
                          Přidělit balík
                        </Button>
                        {canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeOperator(op.userId)}
                            disabled={updateCampaign.isPending}
                            className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                          >
                            Odebrat
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// --- Fronta tab ---

function QueueTab({
  queue,
  statusFilter,
  onStatusFilterChange,
}: {
  queue: CampaignQueueContact[];
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
        >
          <option value="">Všechny statusy</option>
          <option value="pending">Čeká</option>
          <option value="assigned">Přiřazeno</option>
          <option value="in_progress">Probíhá</option>
          <option value="completed">Dokončeno</option>
          <option value="callback">Callback</option>
        </select>
        <span className="text-sm text-gray-500">{queue.length} kontaktů</span>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Kontakt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Hovory
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Callback
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {queue.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    Fronta je prázdná
                  </td>
                </tr>
              ) : (
                queue.map((item) => (
                  <tr key={item.id}>
                    <td className="whitespace-nowrap px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">
                        {item.contact.firstName} {item.contact.lastName || ''}
                      </p>
                      <p className="text-sm text-gray-500">
                        {item.contact.phone || item.contact.email || '-'}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <Badge variant="default">
                        {QUEUE_STATUS_LABELS[item.status] || item.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {item.callCount}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatDate(item.callbackAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// --- Odměny tab ---

function RewardsTab({ campaignId }: { campaignId: string }) {
  const { data: pending, isLoading: loadingPending } = usePendingRewards(campaignId);
  const { data: summary } = useRewardsSummary(campaignId);
  const approveRewards = useApproveRewards();
  const rejectRewards = useRejectRewards();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => {
    if (!pending) return;
    setSelectedIds(selectedIds.length === pending.length ? [] : pending.map((r: any) => r.id));
  };

  const handleApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      await approveRewards.mutateAsync({ campaignId, callLogIds: selectedIds });
      setSelectedIds([]);
      toast.success(`${selectedIds.length} odměn schváleno`);
    } catch {
      toast.error('Chyba při schvalování');
    }
  };

  const handleReject = async () => {
    if (selectedIds.length === 0) return;
    const note = prompt('Důvod zamítnutí (volitelné):');
    try {
      await rejectRewards.mutateAsync({ campaignId, callLogIds: selectedIds, note: note || undefined });
      setSelectedIds([]);
      toast.success(`${selectedIds.length} odměn zamítnuto`);
    } catch {
      toast.error('Chyba při zamítání');
    }
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <div className="p-4 text-center">
              <p className="text-sm text-orange-600 font-medium">Čeká na schválení</p>
              <p className="text-2xl font-bold text-orange-700">{summary.pending.total} Kč</p>
              <p className="text-xs text-gray-500">{summary.pending.count} odměn</p>
            </div>
          </Card>
          <Card>
            <div className="p-4 text-center">
              <p className="text-sm text-green-600 font-medium">Schváleno</p>
              <p className="text-2xl font-bold text-green-700">{summary.approved.total} Kč</p>
              <p className="text-xs text-gray-500">{summary.approved.count} odměn</p>
            </div>
          </Card>
          <Card>
            <div className="p-4 text-center">
              <p className="text-sm text-red-600 font-medium">Zamítnuto</p>
              <p className="text-2xl font-bold text-red-700">{summary.rejected.total} Kč</p>
              <p className="text-xs text-gray-500">{summary.rejected.count} odměn</p>
            </div>
          </Card>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Odměny ke schválení ({pending?.length || 0})
            </h3>
            {selectedIds.length > 0 && (
              <div className="flex gap-2">
                <Button size="sm" variant="primary" onClick={handleApprove} disabled={approveRewards.isPending}>
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Schválit ({selectedIds.length})
                </Button>
                <Button size="sm" variant="outline" onClick={handleReject} disabled={rejectRewards.isPending}>
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Zamítnout ({selectedIds.length})
                </Button>
              </div>
            )}
          </div>

          {loadingPending ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !pending || pending.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Žádné odměny ke schválení</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.length === pending.length} onChange={selectAll} className="rounded border-gray-300" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operátor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kontakt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Výsledek</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Odměna</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pending.map((reward: any) => (
                    <tr key={reward.id} className={selectedIds.includes(reward.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selectedIds.includes(reward.id)} onChange={() => toggleSelect(reward.id)} className="rounded border-gray-300" />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {reward.user?.fullName || reward.user?.email || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {reward.campaignContact?.contact?.firstName} {reward.campaignContact?.contact?.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{reward.outcome?.name}</td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">{Number(reward.outcome?.rewardAmount || 0)} Kč</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(reward.createdAt).toLocaleDateString('cs-CZ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
